import Layout from "./components/Layout/Layout";
import PageLoader from "./components/Loader/PageLoader";
import { useState, useEffect, lazy, Suspense } from "react";
import {
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import PrivatesRoutes from "./components/PrivateRoute/PrivatesRoutes";
import { PERMISSIONS } from "./constants/const";
import { getCameraMode, getMode } from "./api/axios.js";

const PassedTickets = lazy(() => import("./components/Reports/PassedTickets"));
const ActiveTickets = lazy(() => import("./components/Reports/ActiveTickets"));
const NonactiveTickets = lazy(() => import("./components/Reports/NonactiveTickets"));
const NonactiveAndExpiredTickets = lazy(() => import("./components/Reports/NonactiveAndExpiredTickets"));
const ExpiredTickets = lazy(() => import("./components/Reports/ExpiredTickets"));
const SoldTickets = lazy(() => import("./components/Reports/SoldTickets"));
const MyIssuedTickets = lazy(() => import("./components/Reports/MyIssuedTickets"));
const IssuedTicketsByUser = lazy(() => import("./components/Reports/IssuedTicketsByUser"));
const ReportSelection = lazy(() => import("./components/ReportSelection/ReportSelection"));
const Period = lazy(() => import("./components/Period/Period"));
const TypeTickets = lazy(() => import("./components/Reports/TypeTickets"));
const CategoryTickets = lazy(() => import("./components/Reports/CategoryTickets"));
const KindTickets = lazy(() => import("./components/Reports/KindTickets"));
const LogCardTickets = lazy(() => import("./components/Reports/LogCardTickets"));
const Login = lazy(() => import("./components/Login/Login"));
const Home = lazy(() => import("./components/Home/Home"));
const PaymentMethod = lazy(() => import("./components/Reports/PaymentMethod"));
const NewReader = lazy(() => import("./components/NewReader/NewReader"));
const NewAppUser = lazy(() => import("./components/NewAppUser/NewAppUser"));
const Monitoring = lazy(() => import("./components/Monitoring/Monitoring"));
const DailyReport = lazy(() => import("./components/Reports/DailyReport"));
const PerTicketNumber = lazy(() => import("./components/Search/PerTicketNumber"));
const PerRegistrationPlate = lazy(() => import("./components/Search/PerRegistrationPlate"));
const Unauthorized = lazy(() => import("./components/Error/Unauthorized"));
const AccessRights = lazy(() => import("./components/Authorization/AccessRights"));
const AdminPasswordReset = lazy(() => import("./components/AdminPasswordReset/AdminPasswordReset"));
const SuccPassedTickets = lazy(() => import("./components/Reports/SuccPassedTickets"));
const TopSuccPassedTickets = lazy(() => import("./components/Reports/TopSuccPassed"));
const ReadersList = lazy(() => import("./components/List/ReadersList"));
const EditReader = lazy(() => import("./components/EDIT/EditReader"));
const EditTicket = lazy(() => import("./components/EDIT/EditTicket"));
const EditTicketByPlate = lazy(() => import("./components/EDIT/EditTicketByPlate.jsx"));
const PriceList = lazy(() => import("./components/PriceList/PriceList"));
const EditItem = lazy(() => import("./components/EDIT/EditItem"));
const NewItem = lazy(() => import("./components/PriceList/NewItem"));
const NewTicket = lazy(() => import("./components/NewTicket/NewTicket"));
const QRGenerateTicket = lazy(() => import("./components/NewTicket/QRGenerateTicket"));
const ResendMail = lazy(() => import("./components/NewTicket/ResendMail"));
const ChangePassword = lazy(() => import("./components/EditProfile/ChangePassword"));
const NewKind = lazy(() => import("./components/PriceList/NewKind"));
const NewCategory = lazy(() => import("./components/PriceList/NewCategory"));
const NewType = lazy(() => import("./components/PriceList/NewType"));
const EditKind = lazy(() => import("./components/EDIT/EditKind"));
const EditCategory = lazy(() => import("./components/EDIT/EditCategory"));
const EditType = lazy(() => import("./components/EDIT/EditType"));
const SearchUsers = lazy(() => import("./components/Search/SearchUsers"));
const Purchasing = lazy(() => import("./components/Purchasing/Purchasing"));
const Settings = lazy(() => import("./components/Settings/Settings"));
const UpdateDatabase = lazy(() => import("./components/UpdateDatabase/UpdateDatabase"));
const TicketDesign = lazy(() => import("./components/TicketDesigne/TicketDesign"));
const TemplateGroups = lazy(() => import("./components/TemplateGroups/TemplateGroups"));
const TemplateGroupEvents = lazy(() => import("./components/TemplateGroupEvents/TemplateGroupEvents"));
const TicketTemplates = lazy(() => import("./components/TicketTemplates/TicketTemplates"));
const TemplateManagement = lazy(() => import("./components/TemplateManagement/TemplateManagement"));
const HIK = lazy(() => import("./components/HIK/HIK"));
const Logs = lazy(() => import("./components/Logs/Logs"));
const Refund = lazy(() => import("./components/Refund/Refund"));
const FiscalizationReports = lazy(() => import("./components/FiscalizationReports/FiscalizationReports"));
const NewEmployee = lazy(() => import("./components/NewEmployee/NewEmployee"));
const Employees = lazy(() => import("./components/List/Employees"));
const EditEmployee = lazy(() => import("./components/EDIT/EditEmployee"));
const NewOrganizationUnit = lazy(() => import("./components/NewOrganizationUnit/NewOrganizationUnit"));
const OrganizationUnits = lazy(() => import("./components/List/OrganizationUnits"));
const EditOrganizationalUnit = lazy(() => import("./components/EDIT/EditOrganizationalUnit"));
const NewArticle = lazy(() => import("./components/NewArticle/NewArticle"));
const EditArticle = lazy(() => import("./components/EDIT/EditArticle"));
const Articles = lazy(() => import("./components/List/Articles"));
const NewUnitOfMeasure = lazy(() => import("./components/NewUnitOfMeasure/NewUnitOfMeasure"));
const UnitsOfMeasure = lazy(() => import("./components/List/UnitsOfMeasure"));
const EditUnitOfMeasure = lazy(() => import("./components/EDIT/EditUnitOfMeasure"));
const NewIngredient = lazy(() => import("./components/NewIngredient/NewIngredient"));
const Ingredients = lazy(() => import("./components/List/Ingredients"));
const EditIngredient = lazy(() => import("./components/EDIT/EditIngredient"));
const DailyArticles = lazy(() => import("./components/Reports/DailyArticle.jsx"));
const LockerArangment = lazy(() => import("./components/LockerArangment/LockerArangment.jsx"));
const Lockers = lazy(() => import("./components/Lockers/Lockers.jsx"));
const LockersMonitoring = lazy(() => import("./components/LockersMonitoring/LockersMonitoring.jsx"));
const ParkingLots = lazy(() => import("./components/ParkingLots/ParkingLots.jsx"));
const CameraMonitor = lazy(() => import("./components/CameraMonitor/CameraMonitor.jsx"));

function App() {
  const [mode, setMode] = useState("");
  const [cameraMode, setCameraMode] = useState("");
  useEffect(() => {
    const fetchMode = async () => {
      const res = await getMode();
      const camRes = await getCameraMode();
      setMode(res);
      setCameraMode(camRes);
    };
    fetchMode();
  }, []);
  return (
    <Suspense fallback={<PageLoader loading={true} fullHeight />}>
      <Routes>
        <Route path="/monitor" element={<Monitoring />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/login" element={<Login />} />

        <Route path="/" element={<Layout />}>
          <Route index element={<ReportSelection />} />
          <Route element={<PrivatesRoutes permission={PERMISSIONS.VIEW_HOME} />}>
            <Route path="home" element={<Home />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.VIEW_ACTIVE} />}
          >
            <Route path="active" element={<ActiveTickets />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.VIEW_PASSED} />}
          >
            <Route path="passed" element={<PassedTickets />} />
            <Route path="succ_passed" element={<SuccPassedTickets />} />
            <Route path="top_succ_passed" element={<TopSuccPassedTickets />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.VIEW_NONACTIVE} />}
          >
            <Route path="nonactive" element={<NonactiveTickets />} />
            <Route path="nonactive-and-expired-tickets" element={<NonactiveAndExpiredTickets />} />
            <Route path="expired-in-period" element={<ExpiredTickets />} />
          </Route>
          <Route element={<PrivatesRoutes permission={PERMISSIONS.VIEW_SOLD} />}>
            <Route path="sold" element={<SoldTickets />} />
            <Route path="issued-tickets-by-user" element={<IssuedTicketsByUser />} />
          </Route>
          <Route element={<PrivatesRoutes permission={PERMISSIONS.VIEW_HOME} />}>
            <Route path="my-issued-tickets" element={<MyIssuedTickets />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.VIEW_CATEGORY} />}
          >
            <Route path="category" element={<CategoryTickets />} />
          </Route>
          <Route element={<PrivatesRoutes permission={PERMISSIONS.VIEW_TYPE} />}>
            <Route path="type" element={<TypeTickets />} />
          </Route>
          <Route element={<PrivatesRoutes permission={PERMISSIONS.VIEW_KIND} />}>
            <Route path="kind" element={<KindTickets />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.VIEW_LOG_CARD} />}
          >
            <Route path="log_card" element={<LogCardTickets />} />
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.VIEW_PAYMENT_METHOD} />
            }
          >
            <Route path="payment_method" element={<PaymentMethod />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.VIEW_MONITORING} />}
          >
            <Route path="monitoring" element={<Monitoring />} />
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.CREATE_NEW_READER} />
            }
          >
            <Route path="new-reader" element={<NewReader />} />
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.CREATE_NEW_TICKET} />
            }
          >
            <Route path="new-ticket" element={<NewTicket />} />
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.CREATE_NEW_TICKET} />
            }
          >
            <Route path="new-paper-ticket" element={<QRGenerateTicket />} />
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.CREATE_NEW_TICKET} />
            }
          >
            <Route path="resend-mail" element={<ResendMail />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.CREATE_NEW_USER} />}
          >
            <Route path="new-user" element={<NewAppUser />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.VIEW_DAILYREP} />}
          >
            <Route path="dailyrep" element={<DailyReport />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.VIEW_TICKET_INFO} />}
          >
            <Route path="ticket-info/:ticket_id" element={<PerTicketNumber />} />
          </Route>

          {mode == "1" ? (
            <>
              <Route
                element={
                  <PrivatesRoutes permission={PERMISSIONS.VIEW_TEMPLATE_GROUPS} />
                }
              >
                <Route path="template-groups" element={<TemplateGroups />} />
              </Route>
              <Route
                element={
                  <PrivatesRoutes
                    permission={PERMISSIONS.VIEW_TEMPLATE_GROUP_EVENTS}
                  />
                }
              >
                <Route
                  path="template-group-events"
                  element={<TemplateGroupEvents />}
                />
              </Route>
            </>
          ) : (
            ""
          )}
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.VIEW_TICKET_TEMPLATES} />
            }
          >
            <Route path="ticket-templates" element={<TicketTemplates />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.VIEW_PERIOD} />}
          >
            <Route path="period" element={<Period />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.MANAGE_TEMPLATES} />}
          >
            <Route path="template-management" element={<TemplateManagement />} />
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.ADMIN_RESET_PASSWORD} />
            }
          >
            <Route path="admin-reset-password" element={<AdminPasswordReset />} />
          </Route>
          {cameraMode == "1" ? (
            <>
              <Route
                element={
                  <PrivatesRoutes permission={PERMISSIONS.VIEW_TICKET_INFO} />
                }
              >
                <Route
                  path="plates-info/:registration_plates"
                  element={<PerRegistrationPlate />}
                />
              </Route>
              <Route
                element={<PrivatesRoutes permission={PERMISSIONS.EDIT_TICKET} />}
              >
                <Route
                  path="plate-ticket/edit/:registration_plates"
                  element={<EditTicketByPlate />}
                />
              </Route>

              <Route element={<PrivatesRoutes permission={PERMISSIONS.HIK} />}>
                <Route path="hik" element={<HIK />} />
              </Route>
              <Route element={<PrivatesRoutes permission={PERMISSIONS.HIK} />}>
                <Route path="camera-monitor" element={<CameraMonitor />} />
              </Route>
            </>
          ) : (
            ""
          )}

          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.VIEW_ACCESS_RIGHTS} />
            }
          >
            <Route path="access" element={<AccessRights />} />
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.VIEW_LIST_READERS} />
            }
          >
            <Route path="readers" element={<ReadersList />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.EDIT_READER} />}
          >
            <Route path="readers/edit/:id" element={<EditReader />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.EDIT_TICKET} />}
          >
            <Route path="ticket/edit/:ticket_id" element={<EditTicket />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.EDIT_SETTINGS} />}
          >
            <Route path="settings" element={<Settings />} />
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.VIEW_UPDATE_DATABASE} />
            }
          >
            <Route path="update-database" element={<UpdateDatabase />} />
          </Route>
          <Route>
            <Route path="change-password" element={<ChangePassword />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.VIEW_PRICELIST} />}
          >
            <Route path="price-list" element={<PriceList />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.EDIT_PRICELIST} />}
          >
            <Route path="price-list/edit/:item_id" element={<EditItem />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.CREATE_NEW_ITEM} />}
          >
            <Route path="new-item" element={<NewItem />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.CREATE_NEW_KIND} />}
          >
            <Route path="add-kind" element={<NewKind />} />
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.CREATE_NEW_CATEGORY} />
            }
          >
            <Route path="add-category" element={<NewCategory />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.CREATE_NEW_TYPE} />}
          >
            <Route path="add-type" element={<NewType />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.CREATE_NEW_CATEGORY} />}
          >
            <Route path="categories/edit/:id" element={<EditCategory />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.CREATE_NEW_KIND} />}
          >
            <Route path="kinds/edit/:id" element={<EditKind />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.CREATE_NEW_TYPE} />}
          >
            <Route path="types/edit/:id" element={<EditType />} />
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.VIEW_SEARCH_USERS} />
            }
          >
            <Route path="search-users" element={<SearchUsers />} />
          </Route>

          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.VIEW_PURCHASING} />}
          >
            <Route path="purchasing" element={<Purchasing />} />
          </Route>

          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.TICKET_DESIGN} />}
          >
            <Route path="ticket-design" element={<TicketDesign />} />
          </Route>

          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.REFUND} />}
          >
            <Route path="refund" element={<Refund />} />
          </Route>

          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.FISCALIZATION_REPORTS} />}
          >
            <Route
              path="fiscalization-reports"
              element={<FiscalizationReports />}
            />
          </Route>

          <Route element={<PrivatesRoutes permission={PERMISSIONS.VIEW_LOGS} />}>
            <Route path="logs" element={<Logs />} />
          </Route>

          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.NEW_EMPLOYEE} />}
          >
            <Route path="new-employee" element={<NewEmployee />}></Route>
          </Route>

          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.NEW_EMPLOYEE} />}
          >
            <Route path="employees" element={<Employees />}></Route>
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.NEW_EMPLOYEE} />}
          >
            <Route path="employees/edit/:id" element={<EditEmployee />} />
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.ORGANIZATION_UNITS} />
            }
          >
            <Route
              path="new-organization-unit"
              element={<NewOrganizationUnit />}
            ></Route>
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.ORGANIZATION_UNITS} />
            }
          >
            <Route
              path="organization-units"
              element={<OrganizationUnits />}
            ></Route>
          </Route>
          <Route
            element={
              <PrivatesRoutes permission={PERMISSIONS.ORGANIZATION_UNITS} />
            }
          >
            <Route
              path="organization-units/edit/:id"
              element={<EditOrganizationalUnit />}
            />
          </Route>
          <Route element={<PrivatesRoutes permission={PERMISSIONS.ARTICLE} />}>
            <Route path="new-article" element={<NewArticle />}></Route>
          </Route>
          <Route element={<PrivatesRoutes permission={PERMISSIONS.ARTICLE} />}>
            <Route path="articles" element={<Articles />}></Route>
          </Route>
          <Route element={<PrivatesRoutes permission={PERMISSIONS.ARTICLE} />}>
            <Route path="articles/edit/:id" element={<EditArticle />} />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.UNIT_OF_MEASURE} />}
          >
            <Route
              path="new-unit-of-measure"
              element={<NewUnitOfMeasure />}
            ></Route>
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.UNIT_OF_MEASURE} />}
          >
            <Route path="units-of-measure" element={<UnitsOfMeasure />}></Route>
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.UNIT_OF_MEASURE} />}
          >
            <Route
              path="units-of-measure/edit/:id"
              element={<EditUnitOfMeasure />}
            />
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.INGREDIENTS} />}
          >
            <Route path="new-ingredient" element={<NewIngredient />}></Route>
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.INGREDIENTS} />}
          >
            <Route path="ingredients" element={<Ingredients />}></Route>
          </Route>
          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.INGREDIENTS} />}
          >
            <Route path="ingredients/edit/:id" element={<EditIngredient />} />
          </Route>
          <Route element={<PrivatesRoutes permission={PERMISSIONS.ARTICLE} />}>
            <Route path="canteen_reports" element={<DailyArticles />}></Route>
          </Route>

          <Route
            element={<PrivatesRoutes permission={PERMISSIONS.MANAGE_PARKING_LOTS} />}
          >
            <Route path="parking-lots" element={<ParkingLots />} />
          </Route>

          <Route path="locker-arangment" element={<LockerArangment />}></Route>
          <Route path="lockers" element={<Lockers />}></Route>
          <Route
            path="lockers-monitoring"
            element={<LockersMonitoring />}
          ></Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
const PerTicketNumberWrapper = (props) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const ticket_id = searchParams.get("ticket_id");
  console.log(ticket_id);
  return <PerTicketNumber ticket_id={ticket_id} />;
};

export default App;
